import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import useMediaQuery from 'hooks/use-media-query';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { routes } from 'utils/routing';

import { StoreLocatorSteps, StoreLocatorViews } from '../types';

export const MPARTICLE_VIEW_EVENT_MAP = {
  [StoreLocatorViews.LIST_VIEW]: 'Selected list view',
  [StoreLocatorViews.MAP_VIEW]: 'Selected map view',
};

export * from '../types';

export default function useStoreLocatorView() {
  const { logNavigationClick } = useMParticleContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const [currentView, setCurrentView] = useState(StoreLocatorViews.LIST_VIEW);
  const [currentStep, setCurrentStep] = useState(StoreLocatorSteps.ADDRESS_SELECTOR);
  const isMobile = useMediaQuery('headerMobile');
  const enableDeliverySplitAddressDetails = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_SPLIT_ADDRESS_DETAILS
  );

  const onBackButtonEvent = useCallback(() => {
    setCurrentStep(StoreLocatorSteps.ADDRESS_SELECTOR);
  }, []);

  const onSetCurrentStep = useCallback(
    (step: StoreLocatorSteps) => {
      if (!isMobile || !enableDeliverySplitAddressDetails) {
        return;
      }
      setCurrentStep(step);
    },
    [enableDeliverySplitAddressDetails, isMobile]
  );

  useEffect(() => {
    window.addEventListener('popstate', onBackButtonEvent);
    return () => {
      window.removeEventListener('popstate', onBackButtonEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCurrentView = useCallback(
    (newView: StoreLocatorViews) => {
      // log view switch event
      logNavigationClick(CustomEventNames.BUTTON_CLICK_STORE_LOCATOR_VIEW, {
        'Selected View': MPARTICLE_VIEW_EVENT_MAP[newView],
      });
      // switch view state
      setCurrentView(newView);

      // If we are on delivery route and select Map view go back to Pick Up
      if (newView === StoreLocatorViews.MAP_VIEW && location.pathname === routes.address) {
        navigate(formatMessage({ id: 'routes.storeLocator' }));
      }
    },
    [formatMessage, location.pathname, logNavigationClick, navigate]
  );

  return {
    currentView,
    currentViewIsList: currentView === StoreLocatorViews.LIST_VIEW,
    currentViewIsMap: currentView === StoreLocatorViews.MAP_VIEW,
    updateStoreLocatorView: updateCurrentView,
    currentStep,
    setCurrentStep: onSetCurrentStep,
  };
}
